import React from "react";
import { motion } from "framer-motion";
import { inr } from "../../lib/format";
import { CountUp } from "../common";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Wallet } from "lucide-react";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);

export function CashflowCard({ cashflow, language, item }) {
  const cf = cashflow || [];
  const seasonIncome = cf.reduce((s, c) => s + (c.income || 0), 0);
  const seasonExpense = cf.reduce((s, c) => s + (c.expense || 0), 0);
  const net = seasonIncome - seasonExpense;
  const data = cf.map((c) => ({ month: c.month, income: c.income || 0, expense: c.expense || 0 }));

  return (
    <motion.div variants={item} className="agro-glass rounded-[22px] p-5" data-testid="cashflow-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-bold text-[17px] text-[#173f1e] flex items-center gap-2"><Wallet className="h-5 w-5 text-[#2f9e3a]" />{pick(language, "आय और खर्च", "Income & Expenses")}</h2>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1 text-[#2f9e3a] font-semibold"><span className="h-2.5 w-2.5 rounded-full bg-[#2f9e3a]" />{pick(language, "आय", "Income")}</span>
          <span className="flex items-center gap-1 text-[#e07a3a] font-semibold"><span className="h-2.5 w-2.5 rounded-full bg-[#e07a3a]" />{pick(language, "खर्च", "Expense")}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-2xl bg-[#2f9e3a]/8 p-2.5">
          <p className="text-[11px] text-[#6c7a67]">{pick(language, "आय", "Income")}</p>
          <p className="font-display font-bold text-[15px] text-[#2f7d33] leading-tight"><CountUp value={seasonIncome} format={(v) => inr(Math.round(v), true)} /></p>
        </div>
        <div className="rounded-2xl bg-[#e07a3a]/8 p-2.5">
          <p className="text-[11px] text-[#6c7a67]">{pick(language, "खर्च", "Expense")}</p>
          <p className="font-display font-bold text-[15px] text-[#c25a1f] leading-tight"><CountUp value={seasonExpense} format={(v) => inr(Math.round(v), true)} /></p>
        </div>
        <div className="rounded-2xl bg-[#173f1e]/8 p-2.5">
          <p className="text-[11px] text-[#6c7a67]">{pick(language, "शुद्ध", "Net")}</p>
          <p className={`font-display font-bold text-[15px] leading-tight ${net >= 0 ? "text-[#173f1e]" : "text-rose-600"}`}><CountUp value={net} format={(v) => inr(Math.round(v), true)} /></p>
        </div>
      </div>

      <div className="h-40 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
            <defs>
              <linearGradient id="cf-inc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2f9e3a" stopOpacity={0.5} /><stop offset="100%" stopColor="#2f9e3a" stopOpacity={0.03} /></linearGradient>
              <linearGradient id="cf-exp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e07a3a" stopOpacity={0.4} /><stop offset="100%" stopColor="#e07a3a" stopOpacity={0.03} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6c7a67" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
            <Area type="monotone" dataKey="income" stroke="#2f9e3a" strokeWidth={2.5} fill="url(#cf-inc)" isAnimationActive={false} />
            <Area type="monotone" dataKey="expense" stroke="#e07a3a" strokeWidth={2.5} fill="url(#cf-exp)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}
